import { useEffect, useMemo, useState } from "react";
import { useLiveSettings } from "@/hooks/content";
import { PageHeader, StatusBadge, useToast } from "../components/admin-ui";
import {
  liveSourceOptions,
  type LiveControlSettings,
} from "@/mocks/liveAdmin";

export default function LiveTvControlPage() {
  const { record: settings, update } = useLiveSettings();
  const { show } = useToast();

  const [form, setForm] = useState<LiveControlSettings>({ ...settings });
  const [dirty, setDirty] = useState(false);
  const [lastCheck, setLastCheck] = useState<string>("Never");
  const [testResult, setTestResult] = useState<string>("");

  useEffect(() => {
    setForm({ ...settings });
    setDirty(false);
  }, [settings]);

  function set<K extends keyof LiveControlSettings>(key: K, value: LiveControlSettings[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  }

  function save() {
    update(form);
    setDirty(false);
    show("Live TV settings saved.");
  }

  const activeSource = liveSourceOptions.find((s) => s.value === form.sourceType);

  const monitoring = useMemo(() => {
    const src = form.sourceType;
    const url =
      src === "direct" ? form.hlsUrl : src === "youtube" ? form.youtubeUrl : src === "external" ? form.externalUrl : "";

    if (src === "offline") {
      return { status: "offline" as const, label: "L+TV OFFLINE", connection: "Offline — no stream active" };
    }
    if (!url.trim()) {
      return { status: "warning" as const, label: "MISSING STREAM URL", connection: "Configured source, but no URL set" };
    }
    return { status: "live" as const, label: "READY TO BROADCAST", connection: "Stream URL configured" };
  }, [form]);

  function testStream() {
    const src = form.sourceType;
    const url =
      src === "direct" ? form.hlsUrl : src === "youtube" ? form.youtubeUrl : src === "external" ? form.externalUrl : "";

    const now = new Date().toLocaleTimeString();
    setLastCheck(now);

    if (src === "offline") {
      setTestResult("No stream is active. Select a source to test.");
      return;
    }
    if (!url.trim()) {
      setTestResult("No stream URL configured. Add a URL and try again.");
      return;
    }

    let valid = true;
    let note = "";
    if (src === "direct") {
      valid = url.trim().toLowerCase().endsWith(".m3u8");
      note = valid ? "HLS (.m3u8) URL detected." : "HLS stream should end in .m3u8.";
    } else if (src === "youtube") {
      valid = /youtube\.com|youtu\.be|\/watch\?v=/.test(url) || /^[A-Za-z0-9_-]{11}$/.test(url.trim());
      note = valid ? "YouTube URL or video ID detected." : "Enter a valid YouTube Live URL or video ID.";
    }

    setTestResult(
      valid
        ? `${note} The stream looks ready — the final connection will be verified by your streaming server/CDN.`
        : `${note} Please review the URL before going live.`,
    );

    show(valid ? "Stream URL looks valid." : "Stream URL needs review.", valid ? "success" : "error");
  }

  const dotColor =
    monitoring.status === "live" ? "bg-green-500" : monitoring.status === "warning" ? "bg-yellow-500" : "bg-accent-500";
  const dotText =
    monitoring.status === "live"
      ? "text-green-700"
      : monitoring.status === "warning"
        ? "text-yellow-700"
        : "text-accent-700";

  return (
    <div className="space-y-6">
      <PageHeader
        title="Live TV Control Center"
        subtitle="Control the L+TV broadcast source, current program and stream configuration."
        actions={
          <button
            type="button"
            onClick={save}
            disabled={!dirty}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-save-line text-sm" />
            </span>
            Save Settings
          </button>
        }
      />

      {/* Monitoring panel */}
      <div className="bg-white rounded-lg border border-background-200 p-5">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex items-center gap-3">
            <span className={`w-3 h-3 rounded-full ${dotColor} animate-pulse`} aria-hidden="true" />
            <div>
              <p className={`text-sm font-bold tracking-wide ${dotText}`}>{monitoring.label}</p>
              <p className="text-xs text-foreground-500 mt-0.5">{monitoring.connection}</p>
            </div>
          </div>
          <div className="md:ml-auto grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2 text-sm">
            <div>
              <p className="text-[11px] uppercase tracking-wide text-foreground-400">Current source</p>
              <p className="text-foreground-900 font-medium">{activeSource?.label ?? "Offline"}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-foreground-400">Current program</p>
              <p className="text-foreground-900 font-medium">{form.currentProgram || "—"}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-foreground-400">Next program</p>
              <p className="text-foreground-900 font-medium">{form.nextProgram || "—"}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-foreground-400">Last check</p>
              <p className="text-foreground-900 font-medium">{lastCheck}</p>
            </div>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-background-100 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={testStream}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-primary-700 border border-primary-200 rounded-md hover:bg-primary-50 whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-flashlight-line text-sm" />
            </span>
            Test Stream
          </button>
          <button
            type="button"
            onClick={() => {
              setLastCheck(new Date().toLocaleTimeString());
              show("Status refreshed.");
            }}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-foreground-700 border border-background-200 rounded-md hover:bg-background-50 whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-refresh-line text-sm" />
            </span>
            Refresh Status
          </button>
        </div>

        {testResult && (
          <p className="mt-3 text-xs text-foreground-600 bg-background-50 border border-background-100 rounded-md p-3">
            {testResult}
          </p>
        )}
      </div>

      {/* Source selector */}
      <div>
        <h2 className="text-sm font-semibold text-foreground-900 mb-3">Active stream source</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {liveSourceOptions.map((opt) => {
            const active = form.sourceType === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => set("sourceType", opt.value)}
                className={`text-left p-4 rounded-lg border transition-colors ${
                  active
                    ? "border-primary-500 bg-primary-50"
                    : "border-background-200 bg-white hover:border-primary-300"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={`w-9 h-9 flex items-center justify-center rounded-md ${
                      active ? "bg-primary-500 text-white" : "bg-background-100 text-foreground-600"
                    }`}
                  >
                    <i className={`${opt.icon} text-base`} />
                  </span>
                  {active && (
                    <StatusBadge tone="live">
                      <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
                      Active
                    </StatusBadge>
                  )}
                </div>
                <p className={`mt-3 text-sm font-semibold ${active ? "text-primary-700" : "text-foreground-900"}`}>
                  {opt.label}
                </p>
                <p className="mt-1 text-xs text-foreground-500">{opt.hint}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Source URL field */}
      <div className="bg-white rounded-lg border border-background-200 p-5">
        <h2 className="text-sm font-semibold text-foreground-900 mb-3">Stream configuration</h2>

        {form.sourceType === "direct" && (
          <div>
            <label className="block text-sm font-medium text-foreground-800 mb-1.5">HLS URL (.m3u8)</label>
            <input
              type="text"
              value={form.hlsUrl}
              onChange={(e) => set("hlsUrl", e.target.value)}
              placeholder="https://streaming.example.com/ltv/index.m3u8"
              className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
            <p className="mt-1 text-xs text-foreground-500">
              Leave blank until your studio/control-room stream is provided. No credentials are stored here.
            </p>
          </div>
        )}

        {form.sourceType === "youtube" && (
          <div>
            <label className="block text-sm font-medium text-foreground-800 mb-1.5">YouTube Live URL or video ID</label>
            <input
              type="text"
              value={form.youtubeUrl}
              onChange={(e) => set("youtubeUrl", e.target.value)}
              placeholder="https://www.youtube.com/watch?v=…  or  11-character ID"
              className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
          </div>
        )}

        {form.sourceType === "external" && (
          <div>
            <label className="block text-sm font-medium text-foreground-800 mb-1.5">External stream URL</label>
            <input
              type="text"
              value={form.externalUrl}
              onChange={(e) => set("externalUrl", e.target.value)}
              placeholder="https://provider.example.com/stream"
              className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
          </div>
        )}

        {form.sourceType === "offline" && (
          <div className="text-sm text-foreground-600 bg-background-50 border border-background-100 rounded-md p-4">
            L+TV is set to <span className="font-semibold">Offline</span>. Viewers will see the offline message below.
          </div>
        )}
      </div>

      {/* Program info */}
      <div className="bg-white rounded-lg border border-background-200 p-5">
        <h2 className="text-sm font-semibold text-foreground-900 mb-3">Live program details</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground-800 mb-1.5">Live title</label>
            <input
              type="text"
              value={form.liveTitle}
              onChange={(e) => set("liveTitle", e.target.value)}
              className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-800 mb-1.5">Current program</label>
            <input
              type="text"
              value={form.currentProgram}
              onChange={(e) => set("currentProgram", e.target.value)}
              className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-800 mb-1.5">Current presenter</label>
            <input
              type="text"
              value={form.currentPresenter}
              onChange={(e) => set("currentPresenter", e.target.value)}
              className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-800 mb-1.5">Next program</label>
            <input
              type="text"
              value={form.nextProgram}
              onChange={(e) => set("nextProgram", e.target.value)}
              className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-800 mb-1.5">Start time</label>
            <input
              type="time"
              value={form.startTime}
              onChange={(e) => set("startTime", e.target.value)}
              className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-800 mb-1.5">End time</label>
            <input
              type="time"
              value={form.endTime}
              onChange={(e) => set("endTime", e.target.value)}
              className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-foreground-800 mb-1.5">Live description</label>
            <textarea
              value={form.liveDescription}
              onChange={(e) => set("liveDescription", e.target.value)}
              rows={3}
              className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-foreground-800 mb-1.5">Offline message</label>
            <textarea
              value={form.offlineMessage}
              onChange={(e) => set("offlineMessage", e.target.value)}
              rows={2}
              className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-foreground-800 mb-1.5">Thumbnail URL (optional)</label>
            <input
              type="text"
              value={form.thumbnail}
              onChange={(e) => set("thumbnail", e.target.value)}
              placeholder="https://…"
              className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
          </div>
        </div>
      </div>
    </div>
  );
}